import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';

import { pluralize } from './utils/extensionHelpers';
import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface LabelsObject {
  [key: string]: string;
}

interface LabelEntry {
  key: string;
  value: string;
}

const getLabels = (label: LabelEntry[], oldLabels: LabelEntry[]) => {
  return label.filter(n => !oldLabels.find(o => o.key === n.key));
};

const labelsToEntries = (labels: LabelsObject | null): LabelEntry[] => {
  if (!labels || typeof labels !== 'object') {
    return [];
  }
  return Object.entries(labels).map(([key, value]) => ({ key, value }));
};

const LabelsChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldValue = change.oldValue as LabelsObject | null;
  const newValue = change.newValue as LabelsObject | null;

  const oldLabels = labelsToEntries(oldValue);
  const newLabels = labelsToEntries(newValue);

  // Compute diff
  const added = newLabels.filter(n => !oldLabels.find(o => o.key === n.key));
  const removed = oldLabels.filter(o => !newLabels.find(n => n.key === o.key));
  const updated = newLabels.filter(n => {
    const old = oldLabels.find(o => o.key === n.key);
    return old && old.value !== n.value;
  });

  return (
    <>
      {added.length > 0 && (
        <ActivityItem.Section label="Added labels">
          {added.map((label, i) => (
            <ActivityTagItem
              key={i}
              tagLabel={label.key}
              newValue={label.value}
            />
          ))}
        </ActivityItem.Section>
      )}

      {removed.length > 0 && (
        <ActivityItem.Section label="Removed labels">
          {removed.map((label, i) => (
            <ActivityTagItem
              key={i}
              tagLabel={label.key}
              oldValue={label.value}
            />
          ))}
        </ActivityItem.Section>
      )}

      {updated.length > 0 && (
        <ActivityItem.Section label="Updated labels">
          {updated.map((label, i) => {
            const old = oldLabels.find(o => o.key === label.key);
            return (
              <ActivityTagItem
                key={i}
                tagLabel={label.key}
                oldValue={old?.value}
                newValue={label.value}
              />
            );
          })}
        </ActivityItem.Section>
      )}
    </>
  );
};

export const LABELS_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'labels-change',
  predicate: ({ change }) => change.fieldPath === '/labels',
  title: change => {
    const oldValue = change.oldValue as LabelsObject | null;
    const newValue = change.newValue as LabelsObject | null;

    const oldLabels = labelsToEntries(oldValue);
    const newLabels = labelsToEntries(newValue);

    const addedCount = getLabels(newLabels, oldLabels).length;
    const removedCount = getLabels(oldLabels, newLabels).length;
    const updatedCount = newLabels.filter(n => {
      const old = oldLabels.find(o => o.key === n.key);
      return old && old.value !== n.value;
    }).length;

    if (addedCount > 0 && removedCount === 0 && updatedCount === 0) {
      return `Added ${addedCount} ${pluralize(addedCount, 'label')}`;
    }

    if (removedCount > 0 && addedCount === 0 && updatedCount === 0) {
      return `Removed ${removedCount} ${pluralize(removedCount, 'label')}`;
    }

    if (updatedCount > 0 && addedCount === 0 && removedCount === 0) {
      return `Updated ${updatedCount} ${pluralize(updatedCount, 'label')}`;
    }

    return 'Updated labels';
  },
  render: LabelsChangeRender,
});
